import React, { useState, useEffect, useRef } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Upload, X, Image as ImageIcon, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

export const SuperAdminConfiguracionPage = () => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    loadLogo();
    
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  const loadLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_BASE}/configuracion/logo-plataforma`, { headers });
      
      if (response.ok) {
        const blob = await response.blob();
        if (blob.size > 0) {
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
          }
          const imageUrl = URL.createObjectURL(blob);
          blobUrlRef.current = imageUrl;
          setLogoPreview(imageUrl);
          setHasLogo(true);
        }
      } else {
        setHasLogo(false);
        setLogoPreview(null);
      }
    } catch (error) {
      setHasLogo(false);
      setLogoPreview(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo se permiten archivos de imagen');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('El archivo no debe superar 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!logoFile) {
      toast.error('Selecciona un archivo primero');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);

      await api.post('/configuracion/logo-plataforma', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Logo de plataforma actualizado');
      setHasLogo(true);
      setLogoFile(null);
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete('/configuracion/logo-plataforma');
      toast.success('Logo de plataforma eliminado');
      
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      
      setLogoPreview(null);
      setLogoFile(null);
      setHasLogo(false);
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error('Error al eliminar el logo');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLogoFile(null);
    if (hasLogo) {
      await loadLogo();
    } else {
      setLogoPreview(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="superadmin-configuracion-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Configuración de Plataforma</h1>
        <p className="text-gray-600 mt-1">Personaliza la apariencia general del sistema</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Logo de la Plataforma</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-base font-semibold">Logo de Inicio de Sesión</Label>
            <p className="text-sm text-gray-600 mt-1 mb-4">
              Este logo aparecerá en la página de inicio de sesión para todos los usuarios del sistema.
              Se recomienda usar formato PNG con fondo transparente.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-medium mb-2 block">Vista Previa</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 flex items-center justify-center bg-gray-50 min-h-[200px]">
                  {logoPreview ? (
                    <div className="text-center">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="max-h-32 mx-auto object-contain"
                      />
                      {logoFile && (
                        <div className="mt-3 flex items-center justify-center gap-2 text-sm text-green-600">
                          <Check size={16} />
                          <span>Nuevo logo seleccionado</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <ImageIcon size={48} className="mx-auto mb-2" />
                      <p className="text-sm">No hay logo cargado</p>
                      <p className="text-xs mt-1">Se mostrará el logo por defecto</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="logo-upload" className="text-sm font-medium mb-2 block">
                    Seleccionar Archivo
                  </Label>
                  <div className="flex flex-col gap-3">
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="logo-file-input"
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => document.getElementById('logo-upload').click()}
                        data-testid="select-logo-button"
                      >
                        <Upload size={18} className="mr-2" />
                        Seleccionar Imagen
                      </Button>
                    </label>

                    {logoFile && (
                      <div className="bg-blue-50 p-3 rounded-md text-sm">
                        <p className="font-medium text-blue-900">Archivo seleccionado:</p>
                        <p className="text-blue-700 truncate">{logoFile.name}</p>
                        <p className="text-blue-600 text-xs mt-1">
                          {(logoFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    )}

                    <div className="text-xs text-gray-500 space-y-1">
                      <p>• Formatos: PNG, JPG, GIF</p>
                      <p>• Tamaño máximo: 5MB</p>
                      <p>• Recomendado: PNG con fondo transparente</p>
                      <p>• Dimensiones sugeridas: 300x80px</p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  {logoFile && (
                    <Button
                      onClick={handleUpload}
                      disabled={loading}
                      className="w-full"
                      data-testid="upload-logo-button"
                    >
                      {loading ? 'Subiendo...' : 'Subir Logo de Plataforma'}
                    </Button>
                  )}

                  {logoFile && (
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      disabled={loading}
                      className="w-full"
                    >
                      Cancelar
                    </Button>
                  )}

                  {hasLogo && !logoFile && (
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={loading}
                      className="w-full"
                      data-testid="delete-logo-button"
                    >
                      <X size={18} className="mr-2" />
                      Eliminar Logo Actual
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Nota:</strong> Este logo es visible para todos los usuarios en la página de inicio de sesión.
              Cada empresa puede configurar su propio logo que aparecerá en su menú lateral.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
